import type { MotionValue } from "framer-motion";
import {
  CARD_ENTRANCE_BASE_DELAY_MS,
  CARD_ENTRANCE_DELAY_PER_MS,
  CARD_TEXT_COLOR,
  CARD_TEXT_COLOR_FOCUSED,
  DIMMED_OPACITY,
  EVENT_SEPARATOR_COLOR,
  NODE_BLOOM_RING_SIZE,
  NODE_ENTRANCE_DELAY_PER_MS,
  NODE_FOCUSED_SCALE,
  NODE_TO_CARD_GAP,
  NODE_TO_LABEL_GAP,
  PERIOD_LABEL_HEIGHT,
  PERIOD_NODE_SIZE,
  PERIOD_SPACING,
  PERIOD_WASH_WIDTH,
  PROXIMITY_RADIUS_PX,
} from "./timeline-constants";

export enum TimelineDotState {
  Past = "past",
  Now = "now",
  Future = "future",
}

export const TIMELINE_DOT_STATE = TimelineDotState;

export const CSS_BOX_SHADOW_NONE = "none";
export const SCROLL_DIRECTION_VERTICAL = "vertical";
export const TIMELINE_LABEL_TEXT_ALIGN_CENTER = "center";

const DEFAULT_SECTION_RGB: RgbColor = {
  red: 102,
  green: 254,
  blue: 193,
};
const FULL_PROXIMITY_THRESHOLD = 0.99;
const CARD_TEXT_ALPHA_BASE = 0.72;
const CARD_TEXT_ALPHA_FOCUS_BONUS = 0.2;
const PERIOD_WASH_HEIGHT_RATIO = 0.8;
const PERIOD_WASH_GRADIENT_HEIGHT_RATIO = 0.35;
const PERIOD_WASH_ALPHA = 0.047;
const CARD_INITIAL_OFFSET_PX = 12;
const PERIOD_LABEL_MAX_WIDTH_GUTTER = 20;
const HEX_COLOR_REGEX = /^#?([\dA-Fa-f]{2})([\dA-Fa-f]{2})([\dA-Fa-f]{2})$/;

export interface RgbColor {
  red: number;
  green: number;
  blue: number;
}

export interface TimelineEventStackStyle {
  bottom?: number;
  top?: number;
  flexDirection: "column" | "column-reverse";
}

export interface TimelineDotVisual {
  background: string;
  boxShadow: string;
}

export interface TimelineEventCardModel {
  borderTop: string | undefined;
  delaySeconds: number;
  initialY: number;
}

const TIMELINE_DOT_VISUALS: Record<TimelineDotState, TimelineDotVisual> = {
  [TimelineDotState.Past]: {
    background: "rgba(255, 255, 255, 0.5)",
    boxShadow: CSS_BOX_SHADOW_NONE,
  },
  [TimelineDotState.Now]: {
    background: "#66FEC1",
    boxShadow:
      "0 0 0 4px rgba(102, 254, 193, 0.12), 0 0 14px rgba(102, 254, 193, 0.5)",
  },
  [TimelineDotState.Future]: {
    background: "transparent",
    boxShadow: "inset 0 0 0 2px rgba(102, 254, 193, 0.45)",
  },
};

const TIMELINE_DOT_SCALE_SELECTORS: Record<
  TimelineDotState,
  (nodeCoreScale: MotionValue<number>) => MotionValue<number> | number
> = {
  [TimelineDotState.Past]: () => 1,
  [TimelineDotState.Now]: (nodeCoreScale) => nodeCoreScale,
  [TimelineDotState.Future]: () => 1,
};

function clamp(input: {
  value: number;
  min: number;
  max: number;
}): number {
  return Math.min(input.max, Math.max(input.min, input.value));
}

function hexToRgb(input: { hexColor: string }): RgbColor | null {
  const hexColorMatch = input.hexColor.trim().match(HEX_COLOR_REGEX);
  if (!hexColorMatch) {
    return null;
  }

  const [, redHex = "", greenHex = "", blueHex = ""] = hexColorMatch;

  return {
    red: Number.parseInt(redHex, 16),
    green: Number.parseInt(greenHex, 16),
    blue: Number.parseInt(blueHex, 16),
  };
}

export function toTimelineRgba(input: {
  rgb: RgbColor;
  alpha: number;
}): string {
  const alpha = clamp({ value: input.alpha, min: 0, max: 1 });

  return `rgba(${input.rgb.red}, ${input.rgb.green}, ${input.rgb.blue}, ${alpha})`;
}

export function selectTimelineSectionRgb(input: {
  sectionColor: string;
}): RgbColor {
  return hexToRgb({ hexColor: input.sectionColor }) ?? DEFAULT_SECTION_RGB;
}

export function selectTimelinePeriodCenterX(input: {
  periodGlobalIndex: number;
}): number {
  return input.periodGlobalIndex * PERIOD_SPACING + PERIOD_SPACING / 2;
}

export function selectTimelinePeriodWashHeight(input: {
  chartHeight: number;
}): number {
  return input.chartHeight * PERIOD_WASH_HEIGHT_RATIO;
}

export function selectTimelinePeriodWashGradient(input: {
  chartHeight: number;
  sectionRgb: RgbColor;
}): string {
  return `radial-gradient(ellipse ${PERIOD_WASH_WIDTH / 2}px ${
    input.chartHeight * PERIOD_WASH_GRADIENT_HEIGHT_RATIO
  }px at 50% 50%, ${toTimelineRgba({
    rgb: input.sectionRgb,
    alpha: PERIOD_WASH_ALPHA,
  })} 0%, transparent 100%)`;
}

export function selectNeedleProximity(input: {
  needleActive: boolean;
  periodCenterX: number;
  needleX: number;
}): number {
  if (!input.needleActive) {
    return 0;
  }

  const distance = Math.abs(input.periodCenterX - input.needleX);
  if (distance >= PROXIMITY_RADIUS_PX) {
    return 0;
  }

  const proximity = clamp({
    value: 1 - distance / PROXIMITY_RADIUS_PX,
    min: 0,
    max: 1,
  });

  return proximity * proximity * (3 - 2 * proximity);
}

export function selectTimelinePeriodOpacity(input: {
  needleActive: boolean;
  proximity: number;
}): number {
  if (!input.needleActive) {
    return 1;
  }

  return DIMMED_OPACITY + (1 - DIMMED_OPACITY) * input.proximity;
}

export function selectTimelineWashOpacity(input: {
  needleActive: boolean;
  proximity: number;
}): number {
  if (!input.needleActive) {
    return 0;
  }

  return input.proximity;
}

export function selectTimelineCardTextColor(input: {
  needleActive: boolean;
  proximity: number;
}): string {
  if (!input.needleActive) {
    return CARD_TEXT_COLOR;
  }

  if (input.proximity >= FULL_PROXIMITY_THRESHOLD) {
    return CARD_TEXT_COLOR_FOCUSED;
  }

  const alpha =
    CARD_TEXT_ALPHA_BASE + CARD_TEXT_ALPHA_FOCUS_BONUS * input.proximity;

  return `rgba(255, 255, 255, ${alpha})`;
}

export function selectTimelineNodeCoreScale(input: {
  needleActive: boolean;
  proximity: number;
}): number {
  if (!input.needleActive) {
    return 1;
  }

  return 1 + (NODE_FOCUSED_SCALE - 1) * input.proximity;
}

export function selectTimelineLabelTop(input: {
  isAbove: boolean;
  trackY: number;
}): number {
  if (input.isAbove) {
    return input.trackY + NODE_TO_LABEL_GAP;
  }

  return input.trackY - NODE_TO_LABEL_GAP - PERIOD_LABEL_HEIGHT;
}

export function selectTimelinePeriodLabelMaxWidth(): number {
  return PERIOD_SPACING - PERIOD_LABEL_MAX_WIDTH_GUTTER;
}

export function selectTimelineEventStackVisible(input: {
  isCollapsed: boolean;
  eventCount: number;
}): boolean {
  return !input.isCollapsed && input.eventCount > 0;
}

export function selectTimelineEventStackStyle(input: {
  isAbove: boolean;
  chartHeight: number;
  trackY: number;
}): TimelineEventStackStyle {
  if (input.isAbove) {
    return {
      bottom: input.chartHeight - (input.trackY - NODE_TO_CARD_GAP),
      flexDirection: "column-reverse",
    };
  }

  return {
    top: input.trackY + NODE_TO_CARD_GAP,
    flexDirection: "column",
  };
}

export function selectTimelineEventCardModel(input: {
  isAbove: boolean;
  periodGlobalIndex: number;
  eventIndex: number;
  eventCount: number;
}): TimelineEventCardModel {
  const isVisualTop = input.isAbove
    ? input.eventIndex === input.eventCount - 1
    : input.eventIndex === 0;
  const delayMs =
    CARD_ENTRANCE_BASE_DELAY_MS +
    input.periodGlobalIndex * NODE_ENTRANCE_DELAY_PER_MS +
    input.eventIndex * CARD_ENTRANCE_DELAY_PER_MS;

  return {
    borderTop: isVisualTop ? undefined : `1px solid ${EVENT_SEPARATOR_COLOR}`,
    delaySeconds: delayMs / 1000,
    initialY: input.isAbove ? CARD_INITIAL_OFFSET_PX : -CARD_INITIAL_OFFSET_PX,
  };
}

export function selectTimelineNodeEntranceDelaySeconds(input: {
  periodGlobalIndex: number;
}): number {
  return (200 + input.periodGlobalIndex * NODE_ENTRANCE_DELAY_PER_MS) / 1000;
}

export function selectTimelineDotVisual(input: {
  dotState: TimelineDotState;
}): TimelineDotVisual {
  return TIMELINE_DOT_VISUALS[input.dotState];
}

export function selectTimelineDotScale(input: {
  dotState: TimelineDotState;
  nodeCoreScale: MotionValue<number>;
}): MotionValue<number> | number {
  return TIMELINE_DOT_SCALE_SELECTORS[input.dotState](input.nodeCoreScale);
}

export function selectTimelineBloomRingInitialScale(): number {
  return PERIOD_NODE_SIZE / NODE_BLOOM_RING_SIZE;
}
